import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AccountsService } from '../services/accounts.service';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, filter, map, withLatestFrom } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import {
  CardAccount,
  LoanAccountContext,
  SelfServiceableAccounts,
  SelfServiceableAccountsPayload,
} from '../models/accounts.model';
import { selectAccounts } from './accounts.selector';
import { selectCustomerAttributes } from '../../customer-attributes/store/customer-attributes.selector';
import { CardAttributes, DaysPastDueAttributes } from '../../customer-attributes/models/customer-attributes.model';
import { loadAccounts, loadAccountsSuccess, loadAccountsError, updateAccountsWithCasData } from './accounts.action';
import { getPastDueLoanAccount, getHasPastDueCardAccount } from '../util/accounts.util';

@Injectable()
export class AccountsEffects {
  constructor(private actions$: Actions, private accountsService: AccountsService, private readonly store: Store) {}

  getAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAccounts),
      withLatestFrom(this.store.select(selectAccounts)),
      exhaustMap(([, cachedAccounts]) => {
        if (cachedAccounts) {
          // If accounts already present, use cached value
          // (CAS data account effect will still run so no need to worry about checking)
          return of(loadAccountsSuccess({ data: { ...cachedAccounts } }));
        }
        return this.accountsService.getSelfServicableAccounts().pipe(
          map((accounts) => loadAccountsSuccess({ data: accounts })),
          catchError((error) => of(loadAccountsError({ error: error })))
        );
      })
    )
  );

  updateAccountsWithCasData$ = createEffect(() =>
    combineLatest([this.actions$.pipe(ofType(loadAccountsSuccess)), this.store.select(selectCustomerAttributes)]).pipe(
      filter(
        ([, customerAttributes]) =>
          !!customerAttributes && !!customerAttributes.cardSummary && !!customerAttributes.loanDaysPastDue
      ),
      map(([action, customerAttributes]) => {
        const { loanDaysPastDue, cardSummary } = customerAttributes;
        const { bechAccounts } = action.data;
        const accounts: SelfServiceableAccounts = {
          pastDueLoanAccount: getPastDueLoanAccount(action.data.loanAccounts[0]?.accountDetailsList),
          hasPastDueCardAccount: getHasPastDueCardAccount(action.data.cardAccounts),
          loanAccounts: mapLoanSummaryPropsToAccounts(action.data, loanDaysPastDue),
          cardAccounts: mapCardSummaryPropsToAccounts(action.data, cardSummary),
          bechAccounts: [...bechAccounts],
        };
        return accounts;
      }),
      map((mappedAccounts) => updateAccountsWithCasData({ data: mappedAccounts }))
    )
  );
}

// Helper pulled from customer hub: customer-gateway response does not include balance/status
// so those vals must be pulled from CAS card summaries (join on card last 4)
const mapCardSummaryPropsToAccounts = (
  acctsPayload: SelfServiceableAccountsPayload,
  cardSummaries: CardAttributes[] = []
): CardAccount[] => {
  const cardAccounts = acctsPayload.cardAccounts ?? [];
  return cardAccounts.map((account) => {
    const { accountStatus, balance } =
      cardSummaries.find((summary) => summary?.lastFourCardNumber === account?.lastFourCardNumber) || {};
    return { ...account, balance, accountStatus };
  });
};

const mapLoanSummaryPropsToAccounts = (
  acctsPayload: SelfServiceableAccountsPayload,
  loanSummaries: DaysPastDueAttributes[] = []
): LoanAccountContext[] => {
  const accountContext = acctsPayload.loanAccounts[0];
  const mappedLoanAccounts = (accountContext?.accountDetailsList ?? []).map((account) => {
    const { daysPastDue } =
      loanSummaries.find(
        ({ lastFourAccountNumber }) => lastFourAccountNumber === account.last4DigitsExternalAccountNumber
      ) || {};
    return { ...account, daysPastDue };
  });
  return [
    {
      url: accountContext?.url,
      accountDetailsList: [...mappedLoanAccounts],
    },
  ];
};

export const attributeToAccountMappers = {
  mapCardSummaryPropsToAccounts,
  mapLoanSummaryPropsToAccounts,
};
